import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth, findUserGuild } from '../../contexts/AuthContexte';
import { useAccess } from '../../contexts/AccessContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../api';
import AdminSidebar from './AdminSidebar';
import '../pages/AccueilPage.css';

/**
 * Espace membre d'un serveur : menu latéral + page courante.
 * Les pages liées à la réservation n'apparaissent que si la fonctionnalité est activée sur le serveur.
 */
const MemberLayout = () => {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, token } = useAuth();
    const { can } = useAccess();
    const { t } = useLanguage();

    const [server, setServer] = useState(null);
    // Réservation activée sur ce serveur ? (null tant que la réponse n'est pas arrivée)
    const [reservationEnabled, setReservationEnabled] = useState(null);

    useEffect(() => {
        if (!token) { navigate('/'); return; }
        const guild = findUserGuild(user, serverId);
        setServer(guild || { id: serverId, name: serverId, icon: null });
        if (!guild?.name) {
            api.get(`/servers/${serverId}`)
                .then(res => {
                    const s = res.data?.server;
                    if (s?.name) setServer(prev => ({ ...prev, name: s.name, icon: prev?.icon || s.icon }));
                })
                .catch(() => {});
        }
    }, [user, token, serverId, navigate]);

    useEffect(() => {
        let cancelled = false;
        api.get(`/reservation-config/${serverId}`)
            .then(res => { if (!cancelled) setReservationEnabled(res.data?.enabled !== false); })
            .catch(() => { if (!cancelled) setReservationEnabled(true); });
        return () => { cancelled = true; };
    }, [serverId]);

    const base = `/server/${serverId}/member`;
    const allPages = [
        { path: base, end: true, icon: 'home', label: t('member.nav.general', 'Informations générales') },
        { path: `${base}/perso`, icon: 'user', label: t('member.nav.stats', 'Stats perso') },
        { path: `${base}/personnages`, icon: 'users', label: t('member.nav.personnages', 'Mes personnages') },
        { path: `${base}/preferences`, icon: 'list', label: t('member.nav.preferences', 'Mes préférences'), reservation: true },
        { path: `${base}/fiche`, icon: 'clipboard', label: t('member.nav.sheet', 'Fiche de réservation'), reservation: true },
        { path: `${base}/encheres`, icon: 'hammer', label: t('member.nav.auctions', 'Enchères'), reservation: true }
    ];
    const pages = allPages.filter(page => !page.reservation || reservationEnabled);

    // Accès direct à une page de réservation alors que la fonctionnalité est désactivée : retour à l'accueil
    const currentPath = location.pathname.replace(/\/$/, '');
    const onReservationPage = allPages.some(page => page.reservation && currentPath.startsWith(page.path));
    useEffect(() => {
        if (reservationEnabled === false && onReservationPage) navigate(base, { replace: true });
    }, [reservationEnabled, onReservationPage, base, navigate]);

    return (
        <div className="c2-bg admin-shell">
            <AdminSidebar
                server={server}
                pages={pages}
                badge={{ label: t('adminHome.memberArea', 'Espace membre'), tone: 'purple' }}
                switchLink={can(serverId) ? { to: `/server/${serverId}`, label: t('adminHome.adminArea', 'Administration'), icon: 'shield' } : null}
            />
            <main className="admin-shell__content">
                <Outlet context={{ server, reservationEnabled }} />
            </main>
        </div>
    );
};

export default MemberLayout;
